import React, { Dispatch, memo, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import { btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { Cost } from '@r1-frontend/ui-react/experimental/purchase/cost';
import { ACTIONS, CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';
import { getPluralize } from '@r1-frontend/shared/helpers/pluralize';
import { channelPluralize, monthPluralize } from '@r1-frontend/shared/helpers/pluralize/predifined';
import { useWebp } from '@r1-frontend/shared/hooks/useWebp';

import { getButtonText } from '~/src/components/TariffCard/helpers/getButtonText';
import { ISubscriptionBody } from '~/src/components/TariffCard/subscriptions/types';
import WithPromoLabel from '~/src/components/TariffCard/WithPromoLabel';
import { ITariff } from '~/src/entities/formattedTariff';
import isFullBuyVillage from '~/src/features/full-buy/helpers/ab/isFullBuyVillage';

import InternetBlock from './internetBlock';
import Subscriptions from './subscriptions';
import TvBlock from './tvBlock';

import * as ST from './styled';

export interface ITvDescriptions {
    desc: string,
    details?: string,
}

interface IProps {
    tariff: ITariff,
    isAuth?: boolean,
    openModal?: (tariff: ITariff) => void,
    onClickConnect?: () => void,
    tiled?: boolean,
    setSalePackageId?: Dispatch<SetStateAction<number>>,
    setShowRequestFormPopup?: Dispatch<SetStateAction<boolean>>,
    textForButton?: string,
}

const channelText = getPluralize(channelPluralize);
const monthText = getPluralize(monthPluralize);

const getLocalePrice = (price: number) => price.toLocaleString('ru-RU', { maximumFractionDigits: 0 });

export const MAX_ADV_QUANTITY = 2;

const TariffCard = ({
    tariff,
    isAuth = false,
    openModal = () => ({}),
    onClickConnect,
    tiled = false,
    setSalePackageId,
    setShowRequestFormPopup,
    textForButton,
}: IProps): JSX.Element => {
    const {
        additionalProducts,
        alias,
        analog = 0,
        advantages,
        color,
        colorText,
        description,
        hdCount = 0,
        imageBackground,
        imageBackgroundWebp,
        includes,
        isCottage,
        location,
        offerContents,
        price,
        priceAction,
        priceActionPeriod,
        sdCount = 0,
        speed,
        title,
        titleColors = [],
        isNeedRequest,
        salePackageId,
        salePackageLinks,
    } = tariff;
    const isZasmotris = alias?.toString().includes('zasmotris');
    const isConstructor = alias === 'constructor';
    const isMega = alias?.toString().includes('mega');

    const router = useRouter();
    const { query } = router;

    const isWebpUsed = useWebp();

    const [isOpenInternet, setIsOpenInternet] = useState(true);

    const preparedSubscriptions = useMemo(() => {
        const _offerContents: ISubscriptionBody[] = offerContents.map(item => ({
            id: item.id,
            image: item.image,
            imageWebp: item.image_webp,
            width: item.width,
            alt: item.title,
            type: 'subscription',
            sort: item.sort,
        }));

        const _subscriptions: ISubscriptionBody[] = Object.values(salePackageLinks.reduce<Record<number, ISubscriptionBody>>((acc, spl) => {
            if ('subscriptions' in spl && spl.subscriptions) {
                spl.subscriptions.forEach(item => {
                    acc[item.id] = {
                        id: item.id,
                        image: item.image,
                        imageWebp: item.image_webp,
                        width: item.image_width,
                        alt: item.title,
                        type: 'subscription',
                        sort: item.sort,
                    };
                });
            }

            return acc;
        }, {})).sort((a, b) => a.sort && b.sort ? a.sort - b.sort : -1);

        const _additionalProducts: ISubscriptionBody[] = additionalProducts
            ?.filter(({ product_type, required }) => product_type === 'tv' && required)
            .map(({ id, shopItem }) => ({
                id,
                image: shopItem?.image_for_tariff_card || '',
                imageWebp: shopItem?.image_for_tariff_card_webp || '',
                width: shopItem?.image_width,
                alt: shopItem?.title || '',
                type: 'equipment',
            }));

        return [...((_subscriptions && _subscriptions.length) ? _subscriptions : _offerContents), ...(_additionalProducts ?? [])];
    }, [offerContents, additionalProducts]);

    const hasUrlBackground = useMemo(
        () => !!(imageBackgroundWebp?.includes('url') || imageBackground?.includes('url')),
        [imageBackground, imageBackgroundWebp],
    );

    const resultBackground = useMemo(() => {
        const currentBg = (isWebpUsed && imageBackgroundWebp) ? imageBackgroundWebp : imageBackground;
        return hasUrlBackground ? currentBg.replace(/^url|[()]/g, '') : currentBg;
    }, [hasUrlBackground, imageBackground, imageBackgroundWebp, isWebpUsed]);

    const promoPeriod = useMemo(
        () => priceActionPeriod ? `${priceActionPeriod} ${monthText(priceActionPeriod)}` : '',
        [priceActionPeriod],
    );

    const totalChannels = useMemo(() => hdCount + sdCount + analog, [hdCount, sdCount, analog]);

    const withSubscriptions = useMemo(() => !!offerContents?.length, [offerContents?.length]);

    const internetData = useMemo<string[]>(() => {
        return advantages?.length
            ? advantages
                .slice(0, MAX_ADV_QUANTITY)
                .map(advantage => advantage.description)
            : [];
    }, [advantages]);

    const tvData = useMemo<ITvDescriptions[]>(() => {
        const data = [];

        includes?.length && data.push({
            desc: includes[0].description,
        });
        (hdCount || sdCount) && data.push({
            desc: hdCount
                ? `${hdCount}&nbsp;HD&nbsp;${channelText(hdCount)}.&nbsp;`
                : `${sdCount}&nbsp;${channelText(sdCount)}.&nbsp;`,
            details: 'Подробнее',
        });

        return data;
    }, [hdCount, includes, sdCount]);

    const onToggleHandler = useCallback(() => setIsOpenInternet(!isOpenInternet), [isOpenInternet]);
    const isFullBuyVillageRedirect = useMemo(isFullBuyVillage, []);


    const onClickHandler = useCallback((e: React.SyntheticEvent<HTMLDivElement>) => {
        if (setSalePackageId) {
            setSalePackageId(+salePackageId);
        }
        e.preventDefault();

        if (typeof onClickConnect === 'function') {
            return onClickConnect();
        }

        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.tariffCards,
            action: ACTIONS.clickChooseTariff,
            label: `name:${title.toLowerCase()}|package:${title.split(' ')[0].toLowerCase()}|type:${tariff.type === 'bundles' ? 'complex' : 'mono'}`,
        });

        const code = query.code ? `&code=${query.code}` : '';

        const detailPath = !isAuth && location === '/internet'
            ? `/full-buy?tariffName=${alias}&monoTariff=true${code}`
            : `${location}/${alias}${code}`;


        if (isFullBuyVillageRedirect && !!isCottage) {
            const isMono = !isAuth && location === '/internet/mono-village';
            return router.push(`/full-buy?tariffName=${alias}&monoTariff=${isMono}&filters=cottage${code}`);
        }

        !!isCottage && setShowRequestFormPopup
            ? setShowRequestFormPopup(true)
            : router.push(detailPath);
    }, [alias, onClickConnect, isAuth, isCottage, location, router, title, salePackageId, tariff]);

    const typeAndTextForButton = useMemo(() => {
        const isPrivateSector = !isFullBuyVillageRedirect && !!isCottage;

        const text = textForButton ?? getButtonText(isNeedRequest, isConstructor, isPrivateSector);

        return {
            text,
            type: btnTypes.SECONDARY,

        };
    }, [isConstructor, isNeedRequest, textForButton]);


    return (
        <>
            <ST.CardWrapper tiled={tiled}>
                <ST.ContentSection
                    bg={resultBackground}
                    colorText={colorText}
                    bgSize={isConstructor ? 'contain' : 'cover'}
                >
                    <ST.TariffTitle
                        colors={titleColors}
                        withBg={hasUrlBackground}
                        data-test='card-title'
                    >
                        {title}
                    </ST.TariffTitle>

                    <ST.TariffDescription data-test='tariff-desc' dangerouslySetInnerHTML={{ __html: description }}/>

                    {withSubscriptions &&
                        <Subscriptions
                            items={preparedSubscriptions}
                            isZasmotris={isZasmotris}
                            isMega={isMega}
                            salePackageLinks={salePackageLinks}
                            isCottage={isCottage}
                            data-test='tariff-subs'
                        />
                    }

                    <ST.ServicesSection withSubs={withSubscriptions}>
                        {!!speed && (
                            <InternetBlock
                                isAccordion={withSubscriptions && !!tvData.length}
                                withBg={hasUrlBackground}
                                isOpen={isOpenInternet}
                                onToggle={onToggleHandler}
                                speed={speed}
                                advantages={internetData}
                                bgColor={color}
                                colorText={colorText}
                            />
                        )}
                        {!!totalChannels && (
                            <TvBlock
                                isAccordion={withSubscriptions && !!internetData?.length}
                                withBg={hasUrlBackground}
                                isOpen={!isOpenInternet}
                                onToggle={onToggleHandler}
                                totalChannels={totalChannels}
                                tvDescriptions={tvData}
                                bgColor={color}
                                colorText={colorText}
                                openPopup={() => openModal(tariff)}
                            />
                        )}
                    </ST.ServicesSection>
                </ST.ContentSection>

                <ST.PriceSection>
                    {priceAction
                        ? <>
                            <ST.PricesBlock>
                                <ST.TariffPrice data-test='promo-price'>
                                    <Cost isMonthly>
                                        {getLocalePrice(priceAction)}
                                    </Cost>
                                </ST.TariffPrice>
                                <ST.OldPrice>
                                    <Cost isMonthly>
                                        {getLocalePrice(price)}
                                    </Cost>
                                </ST.OldPrice>
                            </ST.PricesBlock>
                            {promoPeriod && <ST.ActionLabel>цена на {promoPeriod}</ST.ActionLabel>}
                        </>
                        : <>
                            <ST.PricesBlock>
                                <ST.TariffPrice data-test='price'>
                                    <Cost isMonthly>
                                        {isConstructor ? `от ${getLocalePrice(price)}` : getLocalePrice(price)}
                                    </Cost>
                                </ST.TariffPrice>
                            </ST.PricesBlock>
                            {isConstructor && <ST.ActionLabel>Больше услуг – меньше цена</ST.ActionLabel>}
                        </>
                    }
                    <ST.StyledBaseButton
                        type={typeAndTextForButton.type}
                        text={typeAndTextForButton.text}
                        onClick={onClickHandler}
                    />
                </ST.PriceSection>
            </ST.CardWrapper>
        </>
    );
};

const TariffCardWithPromo = (props: IProps) => {
    if (props.tariff.promoLabel) {
        const {
            title,
            backgroundColors,
            borderColors,
            imageIcon,
            imageIconWebp,
        } = props.tariff.promoLabel;

        return (
            <WithPromoLabel
                title={title}
                backgroundColors={backgroundColors}
                borderColors={borderColors}
                image={{
                    src: imageIcon,
                    webpSrc: imageIconWebp,
                }}
            >
                <TariffCard {...props}/>
            </WithPromoLabel>
        );
    }

    return <TariffCard {...props}/>;
};

export default memo(TariffCardWithPromo);
